///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Precipitation for the city weather vignette: a fixed pool of billboard
/// quads (vignette_precip.glsl) falling from the cloud base to the plinth.
/// The pool is built once; intensity is a uniform that collapses the quads
/// above the drawn fraction, so rate changes never touch the buffer.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type vec2, type vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Pool size: heavy rain draws all of it, drizzle a few percent
const cPoolSize = 800
// seed (3) + corner (2) + variant (1), six vertices per quad
const cVertexFloats = 6
const cQuadCorners = [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1]
// Generous culling bounds (km): the pool's real extent is set by uniforms
const cBoundsKm = 120

const cRainColor = rgba(0.78, 0.86, 1.0, 0.7)
const cSnowColor = rgba(1.0, 1.0, 1.0, 0.85)

export interface PrecipStyle {
	/** 0..1 fraction of the pool drawn (intensity) */
	density: number
	/** true for snow / sleet, false for rain */
	snow: boolean
	/** km, cloud base the streaks emerge from */
	topY: number
	/** km, ground they land on */
	bottomY: number
	/** km, disc radius they fall within */
	radius: number
	/** xz drift per km of fall (wind slant) */
	slant: vec2
	/** km, streak length / flake size */
	size: number
}

export class VignettePrecipNode extends NodeBase {
	private readonly precipRenderer: PrecipRenderer
	private readonly geometry: PrecipGeometry
	private density = 0
	private currentOpacity = 0

	constructor(context: StovokorContext) {
		super()
		this.geometry = new PrecipGeometry(context)
		this.precipRenderer = new PrecipRenderer(context, this.geometry)
		this.renderer = this.precipRenderer
	}

	public setStyle(style: PrecipStyle): void {
		const r = this.precipRenderer
		this.density = style.density
		r.density.value = style.density
		r.snow.value = style.snow ? 1 : 0
		r.color.value = style.snow ? cSnowColor : cRainColor
		r.topY.value = style.topY
		r.bottomY.value = style.bottomY
		r.radius.value = style.radius
		r.slant.value = style.slant
		r.size.value = style.size
	}

	public set opacity(value: number) {
		this.currentOpacity = value
		this.precipRenderer.opacity.value = value
	}

	public set timeSeconds(value: number) {
		this.precipRenderer.time.value = value
	}

	public override render(state: RenderState): RenderActionSet[] {
		if (this.density <= 0 || this.currentOpacity <= 0.002) {
			return []
		}
		return super.render(state)
	}
}

/** Random disc positions and phases, uploaded once */
class PrecipGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public seed: VertexAttribute
	public corner: VertexAttribute
	public variant: VertexAttribute
	public vertexBuffer: VertexBuffer
	public bounds = new cube(-cBoundsKm, cBoundsKm)
	public boundingSphere = new sphere(cBoundsKm)
	public available: boolean = true

	constructor(context: StovokorContext) {
		this.seed = new VertexAttribute(context, 3)
		this.corner = new VertexAttribute(context, 2)
		this.variant = new VertexAttribute(context, 1)
		this.vertexBuffer = new VertexBuffer(context, [this.seed, this.corner, this.variant])

		// Deterministic pool so every vignette's rain looks the same shape
		let s = 0x9e3779b9
		const rand = (): number => {
			s ^= s << 13; s >>>= 0
			s ^= s >>> 17
			s ^= s << 5; s >>>= 0
			return (s >>> 0) / 4294967296
		}
		const vertices = new Float32Array(cPoolSize * 6 * cVertexFloats)
		let dst = 0
		for (let i = 0; i < cPoolSize; i++) {
			const angle = rand()
			const radius = rand()
			const phase = rand()
			// Variant spreads evenly so density maps linearly to drawn count
			const variant = (i + 0.5) / cPoolSize
			for (let c = 0; c < 6; c++) {
				vertices[dst++] = angle
				vertices[dst++] = radius
				vertices[dst++] = phase
				vertices[dst++] = cQuadCorners[c * 2]
				vertices[dst++] = cQuadCorners[c * 2 + 1]
				vertices[dst++] = variant
			}
		}
		this.vertexBuffer.uploadData(vertices)
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

class PrecipRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public cameraPosition = new UniformDynamic<vec3>()
	public time = new UniformDynamic<number>(0)
	public radius = new UniformDynamic<number>(1)
	public topY = new UniformDynamic<number>(1)
	public bottomY = new UniformDynamic<number>(0)
	public slant = new UniformDynamic<vec2>()
	public snow = new UniformDynamic<number>(0)
	public density = new UniformDynamic<number>(0)
	public size = new UniformDynamic<number>(0.1)
	public opacity = new UniformDynamic<number>(0)
	public color = new UniformDynamic<color>(cRainColor)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("vignette_precip"), geometry, [transparentMaterial])
		// Billboards face the camera but their winding depends on the
		// slant; draw both faces
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		// Camera in the vignette's local frame for the billboarding
		this.cameraPosition.value = state.modelMatrix.invert().multVec3(state.camera.position, 1)
	}
}
